/**
 * @file       Inspection service
 * @version    2.0.0
 * @author     Mehran Majidi
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Observable, map, catchError, of } 
    from 'rxjs';

import { SecureModule } from "@app/secure/secure.module";

import { ERROR } from '@app/constants/Error.constant';
import { Inspection } from '@app/secure/interface/Inspection.interface';
import { TASK } from '@app/constants/Api.constant';
import { CASE_INSPECTION } from '@app/constants/Api.constant';
import { ApiService } from '@app/secure/service/Api.service';
import { CyResponse } from '@app/secure/interface/CyResponse.interface';
import { InspectionsSearchState } from
    '@app/secure/interface/InspectionsSearchState.interface';
import
{
    InspectionTaskInfoResponse,
    TaskBalanceUpdateResult,
    ValidateInspectionNumberResult,
    ValidateCaseNumbersResult
} from '@app/secure/interface/InspectionServiceResponses.interface';

//this service must be in root because it is used during
//login
@Injectable
({
    providedIn: 'root'
})
class InspectionService implements OnDestroy
{
    // inspection subscription
    subscriptions = [];

    inspection$: ReplaySubject<Inspection> = new ReplaySubject<Inspection>(1);

    private inspectionsSearchState: InspectionsSearchState | null;
    private calendarViewDate: Date;
    
    /*
        new ReplaySubject<Inspection> 
        ({
            number: null,
            typeREF: null,
            statusREF: null,
            totalAmount: null,
            totalPaid: null,
            balance: null,
            customerUId: null,
            customerNumber: null,
            customerFirstName: null,
            customerLastName: null,
            officerUId: null,
            officerNumber: null,
            officerFirstName: null,
            officerLastName: null,
            startDate: null,
            startTime: null,
            endDate: null,
            endTime: null,
            duration: null});
 */   
    // selected inspection as observable
    // selectedInspection$ = new ReplaySubject<Inspection>(1);
    
    constructor
    (
        private apiService: ApiService
    ) 
    {
        this.inspectionsSearchState = null;
        this.calendarViewDate = null;
        let cyData;
        let subscription;
        
        if (localStorage.getItem('activeInspectionNumber'))
        {            
            subscription = this.getInspection(
                        localStorage.getItem('activeInspectionNumber'))
            .subscribe(result =>                         
            {
                cyData = result ? result.cyData : null;

                if (!cyData)
                {
                    return;
                }
                
                this.inspection$.next
                ({
                    number: localStorage.getItem('activeInspectionNumber'),
                    typeREF: cyData.typeREF,
                    statusREF: cyData.statusREF,
                    totalAmount: cyData.totalAmount,
                    totalPaid: cyData.totalPaid,
                    balance: cyData.balance,
                    customerUId: cyData.customerUId,
                    customerNumber: cyData.customerNumber,
                    customerName: cyData.customerName,
                    customerFirstName: cyData.customerFirstName,
                    customerLastName: cyData.customerLastName,
                    officerUId: cyData.officerUId,
                    officerNumber: cyData.officerNumber,
                    officerFirstName: cyData.officerFirstName,
                    officerLastName: cyData.officerLastName,
                    startDate: cyData.startDate,
                    startTime: cyData.startTime,
                    //endDate: cyData.endDate,
                    //endTime: cyData.endTime,
                    duration: cyData.duration        
                });                
            },
            error =>
            {
              console.log(error);
            });
            
            this.subscriptions.push(subscription);
            
//            this.selectedInspection$ = this.inspection$;            
        }
        else
        {
            this.inspection$.next
            ({
                number: null,
                typeREF: null,
                statusREF: null,
                totalAmount: null,
                totalPaid: null,
                balance: null,
                customerUId: null,
                customerNumber: null,
                customerName: null,
                customerFirstName: null,
                customerLastName: null,
                officerUId: null,
                officerNumber: null,
                officerFirstName: null,
                officerLastName: null,
                startDate: null,
                startTime: null,
                //endDate: null,
                //endTime: null,
                duration: null        
            });
        }
    }
    
    private getInspection
    (
        inspectionNumber
    )
    {
        let data;
        let requestObj; 
        
        try
        {
            data = 
            {
                taskNumber: inspectionNumber
            };
            
            requestObj = this.apiService.postRaw<InspectionTaskInfoResponse>(
                'task/tasksInfoByNumber',
                data
            );
/*            
            if (response.error == CONSTANTS.ERROR.NO_ERROR)
            {
                console.log('no error');
            }
*/            
        }        
        catch(e)
        {
            console.log(e);
        }

        return requestObj;        
    }

    ngOnDestroy ()
    {
        let i;
        
        for (i=0;i<this.subscriptions.length;i++)
        {
            this.subscriptions[i].unsubscribe();
        }
        
        // reset the subscriptions
        this.subscriptions = [];
    }
    
    setInspection
    (
        inspection: Inspection
    )
    {
        if (inspection)
        {
            if (inspection.number == null)
            {
                localStorage.removeItem('activeInspectionNumber');
            }
            else
            {
                localStorage.setItem('activeInspectionNumber',
                                     inspection.number);
            }
        }
        else
        {
            localStorage.removeItem('activeInspectionNumber');    
        }
        this.inspection$.next(inspection);
    }

    updateInspection
    (
        newInspection: Inspection
    ) 
    {
        this.inspection$.next(newInspection);
    }

    /**
     * @desc   Post a numeric task balance
     * @access public
     * @param  newBalance : balance after the change
     * @param  newTotalAmount : inspection total
     * @param  newTotalPaid : amount already paid
     * @param  activeInspection : inspection being updated
     * @return observable of the balance update result
     */
    public updateInspectionBalance
    (
        newBalance: number,
        newTotalAmount: number,
        newTotalPaid: number,
        activeInspection: Inspection
    )
    {
        let serviceObj;
        let taskAction: TaskBalanceUpdateResult;

        let postData =
        {
            taskData:
            {
                number: activeInspection.number,
                balance: this.parseMoney(newBalance),
                totalAmount: this.parseMoney(newTotalAmount),
                totalPaid: this.parseMoney(newTotalPaid)
            }
        };
  
        serviceObj = this.apiService.postRaw<Record<string, never>>(
            TASK.UPDATE_TASK_BALANCE,
            postData
        )
        .pipe
        (
            map((result: CyResponse<Record<string, never>>) => 
            {
                taskAction = 
                {
                    done: false,
                    error: '',
                };

                if (result && result.error === ERROR.NO_ERROR) 
                {
                    taskAction.done = true;
                } 
                else 
                {
                    taskAction.error = String(ERROR.UPDATE_FAILED);
                }

                return taskAction;
            }),
            catchError(err => 
            {
                console.log(err);
                taskAction = 
                {
                    done: false,
                    error: '',
                };
                try 
                {
                    const ERROR_CODE = JSON.parse(err.message);

                    switch (ERROR_CODE.error) 
                    {
                        case ERROR.INVALID_POST_DATA:
                             taskAction.error = "Invalid post data";
                             break;
                        case ERROR.GENERAL:
                             taskAction.error = "General error";
                             break;
                        case ERROR.UPDATE_FAILED:
                             taskAction.error = "Update failed";
                             break;
                        default:
                             taskAction.error = "Unknown error";
                             break;
                        // end switch
                    }
                } 
                catch (e) 
                {
                    taskAction.error = "Error parsing error message";
                }

                return of(taskAction);
            })
        );

        return serviceObj;
    }

    /**
     * @desc   - validates the inspection number
     * @access - public
     * @param  - string inspectionNumber: the number of the inspection
     * @return - Observable<ValidateInspectionNumberResult>
     */
    public validateInspectionNumber
    (
        inspectionNumber: string
    ): Observable<ValidateInspectionNumberResult>
    {
        let response: ValidateInspectionNumberResult;
        let serviceObj: Observable<ValidateInspectionNumberResult>;

        let postData =
        {
            taskNumber: inspectionNumber
        }

        serviceObj = this.apiService
                       .postRaw<{ available?: boolean }>(
                           TASK.VALIDATE_TASK_NUMBER,
                           postData
                       )
        .pipe(map((result: CyResponse<{ available?: boolean }>) =>
        {
            response = 
            {
                available: false,
                error: '',
            };

            if (result.cyData?.available && result.error === ERROR.NO_ERROR)
            {
                response.available = true;
            }

            return response;
        }),
        catchError(err =>
        {
            console.log(err);
            response =
            {
                available: false,
                error: '',
            };

            try
            {
                const ERROR_CODE = JSON.parse(err.message);
                switch (ERROR_CODE.error)
                {
                    case ERROR.INVALID_POST_DATA:
                        response.error = "Invalid post data";
                        break;
                    default:
                        response.error = "General Error";
                        break;
                }
            }
            catch (e)
            {
                response.error = "Error parsing error message";
            }

            return of(response);
        }));

        return serviceObj;
    }

    /**
     * @desc   - validates the inspection case numbers
     * @access - public
     * @param  - number caseNumberFrom: Starting case number
     * @param  - number caseNumberTo: The last case number
     * @return - Observable<ValidateCaseNumbersResult>
     */
    public validateCaseNumbers
    (
        caseNumberFrom: number,
        caseNumberTo: number
    ): Observable<ValidateCaseNumbersResult>
    {
        let response: ValidateCaseNumbersResult;
        let serviceObj: Observable<ValidateCaseNumbersResult>;

        let postData =
        {
            caseNumberFrom: caseNumberFrom,
            caseNumberTo: caseNumberTo
        }

        serviceObj = this.apiService
                         .postRaw<{ available?: boolean }>(
                             CASE_INSPECTION.VALIDATE_CASE_NUMBERS,
                             postData
                         )
        .pipe(map((result: CyResponse<{ available?: boolean }>) => 
        {
            response = 
            {
                available: false,
                error: '',
            };

            if (result.cyData?.available && result.error === ERROR.NO_ERROR)
            {
                response.available = true;
            }
            else
            {
                response.error = result.error;
            }

            return response;
        }),
        catchError(err =>
        {
            console.log(err);
            response =
            {
                available: false,
                error: '',
            };

            try
            {
                const ERROR_CODE = JSON.parse(err.message);
                switch (ERROR_CODE.error)
                {
                    case ERROR.INVALID_POST_DATA:
                        response.error = "Invalid post data";
                        break;
                    default:
                        response.error = "General Error";
                        break;
                }
            }
            catch (e)
            {
                response.error = "Error parsing error message";
            }

            return of(response);
        }));

        return serviceObj;
    }

    /**
     * @desc Save inspections search form values for return navigation
     * @access public
     * @param state : search form snapshot
     * @return -
     */
    setInspectionsSearchState
    (
        state: InspectionsSearchState
    )
    {
        this.inspectionsSearchState = state;
    }

    /**
     * @desc Get saved inspections search form values
     * @access public
     * @param -
     * @return saved search state or null
     */
    getInspectionsSearchState(): InspectionsSearchState | null
    {
        let result: InspectionsSearchState | null;

        result = this.inspectionsSearchState;

        return result;
    }

    /**
     * @desc Save calendar month for return navigation
     * @access public
     * @param viewDate : selected calendar date
     * @return -
     */
    setCalendarViewDate
    (
        viewDate
    )
    {
        if (viewDate)
        {
            this.calendarViewDate = new Date(viewDate);
        }
        else
        {
            this.calendarViewDate = null;
        }
    }

    /**
     * @desc Get saved calendar month
     * @access public
     * @param -
     * @return saved calendar date or null
     */
    getCalendarViewDate()
    {
        let result;

        result = this.calendarViewDate;

        return result;
    }

    /**
     * @desc   Turn a currency field into a number
     * @access private
     * @param  value : amount from a form or the inspection
     * @return numeric amount, or 0 when the value is empty
     */
    private parseMoney
    (
        value
    ): number
    {
        let result;
        let text;

        result = 0;
        text = '';

        if (typeof value === 'number' && !isNaN(value))
        {
            result = value;
        }
        else if (value !== null &&
            value !== undefined &&
            value !== '')
        {
            text = String(value).replace(/[^0-9.-]/g, '');
            result = parseFloat(text);

            if (isNaN(result))
            {
                result = 0;
            }
        }

        return result;
    }
}

export { InspectionService }
